import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import ConfirmPurgeModal from './ConfirmPurgeModal';
import { STORAGE_KEYS } from '../utils/storage';
import { getAuthData } from '../utils/auth';

const History = () => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editedAltTexts, setEditedAltTexts] = useState({});
  const [updatingAltText, setUpdatingAltText] = useState({});
  const [regeneratingAltText, setRegeneratingAltText] = useState({});
  const [settings, setSettings] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [5, 10, 25, 50];

  const storeId = sessionStorage.getItem(STORAGE_KEYS.STORE_ID);

  const fetchSettings = async () => {
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          setSettings(settingsData.settings);
        }
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const fetchHistory = async () => {
    if (!storeId) {
      setError('Store ID not found in localStorage. Please go to Dashboard first to initialize the connection.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/alttext-history?id=${storeId}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      if (data.success) {
        setHistory(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch history');
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err.message || 'Failed to fetch history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProduct = (productId) => {
    console.log('handleEditProduct', productId);
    productId = productId.split('-')[0];
    // Open Ecwid product editor
    const authData = getAuthData();
    if (authData?.store_id) {
      const productUrl = `https://my.ecwid.com/store/${authData.store_id}#product:mode=edit&id=${productId}&return=products`;
      window.open(productUrl, '_blank');
    }
  };

  const handlePurgeHistory = async () => {
    if (!storeId) {
      setError('Store ID not found');
      return;
    }
    setPurgeLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/alttext-history?id=${storeId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setHistory([]);
        setShowPurgeModal(false);
        setNotification({ type: 'success', message: 'History purged successfully.' });
      } else {
        setError(data.error || 'Failed to purge history');
      }
    } catch (err) {
      console.error('Error purging history:', err);
      setError(err.message || 'Failed to purge history');
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleUpdateAltText = async (item, assetId) => {
    console.log('handleUpdateAltText called with:', { item, assetId });

    // Check if storeId is available
    console.log('Store ID:', storeId);
    if (!storeId) {
      console.log('Store ID check failed - no store ID');
      setNotification({ 
        type: 'error', 
        message: 'Store ID not found. Please refresh the page and try again.' 
      });
      return;
    }

    setUpdatingAltText(prev => ({ ...prev, [assetId]: true }));
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const newAltText = editedAltTexts[assetId] ?? item.alt_text;
      
      // Check if alt text has actually changed
      console.log('Current alt text:', item.alt_text);
      console.log('New alt text:', newAltText);
      console.log('Alt text changed:', newAltText !== item.alt_text);
      
      if (newAltText === item.alt_text) {
        console.log('Alt text check failed - no changes detected');
        setNotification({ 
          type: 'info', 
          message: 'No changes detected. Please edit the alt text before updating.' 
        });
        setUpdatingAltText(prev => ({ ...prev, [assetId]: false }));
        return;
      }
      
      const reqdata = {
        product_id: item.product_id.split('-')[0],
        image_id: item.product_id.split('-')[1],
        alt_text: newAltText,
        store_id: storeId,
        asset_id: assetId,
      };
      
      console.log('Store ID:', storeId);
      console.log('Updating alt text with data:', reqdata);
      
      const res = await fetch(`${apiUrl}/api/products/image/alttext`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqdata),
      });
      
      console.log('Response status:', res.status);
      const data = await res.json();
      console.log('Response data:', data);
      
      if (res.ok) {
        setEditedAltTexts(prev => ({ ...prev, [assetId]: undefined }));
        //fetchHistory();
      } else {
        setNotification({ type: 'error', message: data.error || 'Failed to update alt text.' });
      }
    } catch (error) {
      console.error('Error updating alt text:', error);
      setNotification({ type: 'error', message: error.message || 'Failed to update alt text.' });
    } finally {
      setUpdatingAltText(prev => ({ ...prev, [assetId]: false }));
    }
  };

  const handleRegenerateAltText = async (item, assetId) => {
    // Check if credits are available
    if (!settings || settings.credits_available <= 0) {
      setNotification({ 
        type: 'danger', 
        message: 'You are out of credits. Please <a href="https://alttextify.net/plans" target="_blank" rel="noopener noreferrer">purchase more credits</a> to continue generating alt text.' 
      });
      return;
    }

    setRegeneratingAltText(prev => ({ ...prev, [assetId]: true }));
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const selectedProductObjects = [{
        id: item.product_id.split('-')[0],
        name: item.product_name,
        brand_id: 0 // Default brand ID for Ecwid
      }];

      const response = await fetch(`${apiUrl}/api/bulkproduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storeId: storeId,
          selectedProductObjects: selectedProductObjects
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to regenerate alt text');
      }

      setNotification({ type: 'success', message: `Alt text regeneration started for "${item.product_name}". Please check back in a few minutes.` });
      setEditedAltTexts(prev => ({ ...prev, [assetId]: undefined }));
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to regenerate alt text.' });
    } finally {
      setRegeneratingAltText(prev => ({ ...prev, [assetId]: false }));
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchSettings();
  }, [storeId]);

  // Calculate pagination
  const totalPages = Math.ceil(history.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = history.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchHistory();
    } catch (error) {
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body position-relative">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="mb-0 fw-bold" style={{ textAlign: 'left', fontSize: '1.25rem' }}>Alt Text Manager</h3>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary btn-md"
                    style={{ minWidth: 120 }}
                    disabled={isLoading || isRefreshing}
                    onClick={handleRefresh}
                  >
                    {isRefreshing ? (
                      <span className="loading-dots" role="status" aria-hidden="true"></span>
                    ) : (
                      <>
                        <RefreshCw size={16} className="me-1" />
                        Refresh
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-danger btn-md"
                    style={{ minWidth: 140 }}
                    disabled={isLoading || !history || history.length === 0}
                    onClick={() => setShowPurgeModal(true)}
                  >
                    <Trash2 size={16} className="me-1" />
                    Purge History
                  </button>
                </div>
              </div>
              <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                View and edit alt text for your images. Make manual updates and sync changes with each product individually.
              </p>

              {notification && (
                <div className={`alert alert-${notification.type} mt-3`} role="alert" dangerouslySetInnerHTML={{ __html: notification.message }}>
                </div>
              )}
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="loading-dots mb-3" role="status" style={{ width: '48px', height: '48px', margin: '0 auto' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted" style={{ fontSize: '0.875rem' }}>Loading history...</p>
                </div>
              ) : paginatedHistory && paginatedHistory.length > 0 ? (
                <div className="row g-4">
                  {paginatedHistory.map((item, idx) => {
                    const assetId = item.asset_id || item.image_id || item.product_id;
                    const isSuccess = notification && notification.type === 'success' && updatingAltText[assetId] === false;
                    const isError = notification && notification.type === 'error' && updatingAltText[assetId] === false;
                    return (
                      <div className="col-12 col-md-6 col-lg-4" key={assetId}>
                        <div className="card h-100 border-0 shadow-sm p-4 d-flex flex-column justify-content-between">
                          <div>
                            <div className="d-flex align-items-center mb-3">
                              {item.image_url ? (
                                <img src={item.image_url} alt="Product" className="rounded me-3" style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #e9ecef', background: '#f8f9fb' }} />
                              ) : (
                                <div className="bg-light d-flex align-items-center justify-content-center rounded me-3" style={{ width: 64, height: 64 }}>
                                  <i className="fas fa-image text-muted"></i>
                                </div>
                              )}
                              <div>
                                <div className="fw-bold" style={{ fontSize: '1rem' }}>{item.product_name || '-'}</div>
                                <span className={`badge mb-1 ${(item.status || 'COMPLETED') === 'COMPLETED' ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '0.55rem', padding: '0.1rem 0.3rem', lineHeight: '1', borderRadius: '0.25rem' }}>{item.status || 'COMPLETED'}</span>
                                <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                                  {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                                </div>
                              </div>
                            </div>
                            <textarea
                              className="form-control mb-2"
                              value={editedAltTexts[assetId] !== undefined ? editedAltTexts[assetId] : (item.alt_text || '')}
                              onChange={e => setEditedAltTexts(prev => ({ ...prev, [assetId]: e.target.value }))}
                              rows={3}
                              style={{ resize: 'vertical', fontSize: '0.875rem' }}
                            />
                          </div>
                          <div className="d-flex justify-content-between mt-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleEditProduct(item.product_id)}
                              disabled={updatingAltText[assetId]}
                            >
                              Verify AltText
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                console.log('Update AltText button clicked');
                                console.log('Button disabled state:', { 
                                  updatingAltText: updatingAltText[assetId], 
                                  regeneratingAltText: regeneratingAltText[assetId] 
                                });
                                handleUpdateAltText(item, assetId);
                              }}
                              disabled={updatingAltText[assetId] || regeneratingAltText[assetId]}
                            >
                              {updatingAltText[assetId] ? (
                                <span className="loading-dots" role="status" aria-hidden="true"></span>
                              ) : 'Update AltText'}
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleRegenerateAltText(item, assetId)}
                              disabled={updatingAltText[assetId] || regeneratingAltText[assetId]}
                            >
                              {regeneratingAltText[assetId] ? (
                                <span className="loading-dots" role="status" aria-hidden="true"></span>
                              ) : 'Regenerate'}
                            </button>
                          </div>
                          {isSuccess && (
                            <div className="text-success mt-2" style={{ fontSize: '0.75rem' }}>Alt text updated!</div>
                          )}
                          {isError && (
                            <div className="text-danger mt-2" style={{ fontSize: '0.75rem' }}>{notification.message}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-5 text-muted" style={{ fontSize: '0.875rem' }}>No history found.</div>
              )}
              {/* Pagination Controls */}
              {history && history.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="d-flex align-items-center">
                    <label className="me-2">Items per page:</label>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={itemsPerPage}
                      onChange={e => handleItemsPerPageChange(Number(e.target.value))}
                    >
                      {itemsPerPageOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      <li className="page-item">
                        <span className="page-link">
                          Page {currentPage} of {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmPurgeModal
        isOpen={showPurgeModal}
        title="Purge Alt Text History"
        message="Are you sure you want to mark all alt text history as deleted? This action will hide the history from view but preserve the data."
        confirmText="Mark as Deleted"
        cancelText="Cancel"
        loading={purgeLoading}
        onConfirm={handlePurgeHistory}
        onCancel={() => setShowPurgeModal(false)}
      />
    </div>
  );
};

export default History; 